import React, { useRef, useState } from "react";
import { Link as RouterLink } from "react-router";
import moment from "moment/moment";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import ListItemText from "@mui/material/ListItemText";
import ListItem from "@mui/material/ListItem";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";

import { serverFormat } from "../../../../utils/dates";
import { isBillingReport } from "../../../../utils/notificationMatching";
import { useNotificationCenter } from "../../../../contexts/notificationCenter";

const styles = {
  badge: unread => ({
    "& .MuiBadge-badge": {
      height: 12,
      width: 12,
      borderRadius: 10,
      marginTop: "10px",
      marginRight: "10px",
      display: unread ? "block" : "none",
    },
  }),

  popover: {
    "& .MuiPopover-paper": {
      width: 320,
    },
  },
};

// Bell UI only; background handling lives in NotificationCenterProvider.
export default function Notifications() {
  const ref = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const { notifications, unread, markRead, refetch, openBillingReport } = useNotificationCenter();

  const handleOpen = () => {
    setOpen(true);
    markRead();
    refetch();
  };

  const handleClose = () => setOpen(false);

  const handleNotificationClick = (e, notification) => {
    if (isBillingReport(notification)) {
      e.preventDefault();
      setOpen(false);
      openBillingReport(notification);
    }
    // For non-billing reports, let the default link behavior work
  };

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <Badge id="notificationBadge" color="error" variant="dot" sx={styles.badge(unread)}>
          <IconButton id="notificationIcon" ref={ref} onClick={handleOpen}>
            <NotificationsNoneOutlinedIcon />
          </IconButton>
        </Badge>
      </Tooltip>
      <Popover
        id="notificationsList"
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        sx={styles.popover}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <Box p={2}>
          <Typography fontWeight={500} color="textPrimary">
            Notifications
          </Typography>
        </Box>
        {notifications.length === 0 ? (
          <Box p={2}>
            <Typography fontWeight={500} fontSize={14} color="textPrimary">
              There are no notifications
            </Typography>
          </Box>
        ) : (
          <>
            <List disablePadding>
              {notifications.map((notification, index) => (
                <ListItem
                  id={`notificationItem-${index}`}
                  divider
                  key={notification.id}
                  button
                  component="a"
                  href={notification.link}
                  target="_blank"
                  onClick={(e) => handleNotificationClick(e, notification)}
                >
                  <ListItemText
                    primary={notification.title}
                    primaryTypographyProps={{ variant: "subtitle2", color: "textPrimary" }}
                    secondary={moment(notification.createdAt, serverFormat).format("lll")}
                  />
                </ListItem>
              ))}
            </List>
            <Box p={1} display="flex" justifyContent="center">
              <Button component={RouterLink} size="small" to="/notifications">
                See All
              </Button>
            </Box>
          </>
        )}
      </Popover>
    </>
  );
}
